// src/components/apps.tsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { TextPlugin } from 'gsap/TextPlugin';
import { StarryBackground } from './StarryBackground';

gsap.registerPlugin(TextPlugin);

// Define the type for the props the component will receive
interface AppsPageProps {
  setActiveTab: (tab: string) => void;
}

const AppsPage: React.FC<AppsPageProps> = ({ setActiveTab }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(".reveal-h1", 
        { text: "" },
        { duration: 1.5, text: "Our upcoming apps and projects!", ease: "none", delay: 0.5 }
      );
      gsap.fromTo(".reveal-p", 
        { text: "" },
        { duration: 1.5, text: "Access the XMRmap database wherever you are, on any device.", ease: "none", delay: 1.5 }
      );
      gsap.fromTo(".app-card", 
        { opacity: 0, scale: 0.9, y: 20 },
        { opacity: 1, scale: 1, y: 0, duration: 0.6, ease: 'power3.out', delay: 2, stagger: 0.2 }
      );
    }, containerRef);
    
    return () => ctx.revert();
  }, []);

  return (
    <>
      <StarryBackground />
      <main ref={containerRef} className="max-w-4xl mx-auto p-6 md:p-8 text-white">
        <header className="text-center my-12 md:my-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="reveal-h1"></span>
            <span className="block text-3xl md:text-4xl text-orange-400 mt-2">Applications</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto reveal-p"></p>
        </header>

        <div className="space-y-8">
          {/* Mobile App Card */}
          <div className="app-card bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6 shadow-lg transition-all duration-300 hover:border-orange-500 hover:shadow-2xl">
            <h2 className="text-2xl font-semibold mb-3 flex items-center gap-4">
              Mobile App
              <span className="text-xs font-medium bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full">Soon!</span>
            </h2>
            <p className="text-gray-400 mb-4">Find Monero-accepting businesses on the go with our dedicated Android and iOS applications.</p>
            <ul className="space-y-2 text-gray-300 list-disc list-inside">
              <li>⚡ Real-time location-based searching and filtering.</li>
              <li>🗺️ Interactive map with detailed business information.</li>
              <li>⭐ Save and manage your favorite Monero-friendly establishments.</li>
              <li>🔒 Enhanced privacy features for Monero users.</li>
            </ul>
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <button disabled className="w-full sm:w-auto px-6 py-2 bg-gray-600 text-gray-400 rounded-md cursor-not-allowed">Coming to Android!</button>
              <button disabled className="w-full sm:w-auto px-6 py-2 bg-gray-600 text-gray-400 rounded-md cursor-not-allowed">Coming to iOS!</button>
            </div>
          </div>

          {/* Web App Card */}
          <div className="app-card bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6 shadow-lg transition-all duration-300 hover:border-orange-500 hover:shadow-2xl">
            <h2 className="text-2xl font-semibold mb-3">XMRhub</h2>
            <p className="text-gray-400 mb-4">Access our full database and comprehensive features directly through any modern web browser, no installation required.</p>
             <ul className="space-y-2 text-gray-300 list-disc list-inside">
              <li>🔍 Global search capabilities for Monero-accepting businesses.</li>
              <li>📊 Detailed business profiles including services and contact info.</li>
              <li>🔄 Always up-to-date with the latest verified listings.</li>
            </ul>
            <div className="mt-6">
              {/* This button now navigates to the 'donations' tab */}
              <button 
                onClick={() => setActiveTab('donations')}
                className="inline-block px-6 py-2 bg-orange-500/20 hover:bg-orange-500/40 text-orange-300 rounded-md transition-colors"
              >
                Contribute
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default AppsPage;
